// ConnectionManager: Peer-to-peer signaling, data connections, presence, and user status
// CLEANED VERSION - All duplicates removed, all bugs fixed

import { MAX_RECONNECT_ATTEMPTS, RECONNECT_BACKOFF_BASE, RECONNECT_BACKOFF_CAP } from '../utils/constants';

// Tiny event emitter (on/off/emit) to avoid external deps
class Emitter {
  constructor() { this._e = {}; }
  on(ev, fn) { (this._e[ev] ||= new Set()).add(fn); return () => this.off(ev, fn); }
  off(ev, fn) { const s = this._e[ev]; if (!s) return; s.delete(fn); }
  emit(ev, payload) { const s = this._e[ev]; if (!s) return; for (const fn of Array.from(s)) try { fn(payload); } catch (_) {} }
}

// Lazy import Peer to avoid SSR issues and allow fallback to window.Peer
let PeerCtor = null;
async function getPeerCtor() {
  if (PeerCtor) return PeerCtor;
  try {
    const mod = await import('peerjs');
    PeerCtor = mod.default || mod.Peer;
  } catch (_) {
    if (typeof window !== 'undefined' && window.Peer) PeerCtor = window.Peer;
  }
  return PeerCtor;
}

// Lazy import Socket.IO client
let io = null;
async function getSocketIO() {
  if (io) return io;
  try {
    const mod = await import('socket.io-client');
    io = mod.io || mod.default;
  } catch (_) {
    console.warn('[ConnectionManager] socket.io-client not available');
  }
  return io;
}

export class ConnectionManager {
  constructor({ roomId, peerOptions }) {
    this.roomId = roomId;
    this.peerOptions = this._buildPeerOptions(peerOptions);
    this.emitter = new Emitter();
    this.peer = null;
    this.socket = null;
    this.connections = {}; // { peerId: { text: DataConnection, file: DataConnection } }
    this.users = [];
    this.usersByPeerId = {};
    this.pingInterval = null;
    this.keepAliveInterval = null;
    this._peerLastSeen = {};
    this._dialing = {};
    this._dialingTimers = {}; // Timeout handles to auto-reset _dialing flags
    this._reconnectTimers = {}; // Per-peer reconnect delay timers
    this._iceDisconnectTimers = {}; // Per-peer ICE disconnect grace timers
    this._healthCheckInterval = null;
    this._started = false;
    this._joinedRoom = false; // Track if we've already joined
    this._socketReconnecting = false; // Track Socket.IO reconnection state
    this._reconnectAttempts = {}; // Per-peer reconnect attempt counters (backoff ladder)
    this._reinitTimer = null; // Pending full peer re-init timer
    this._reinitAttempts = 0; // Full peer re-init attempt counter
    this._reinitializing = false; // Guard against concurrent re-inits
    this._destroyed = false; // Set when disconnect() is called intentionally
    this._netListenersBound = false;
    this._boundOnOnline = null;
    this._boundOnOffline = null;
    this._boundOnVisibility = null;
    console.log('[ConnectionManager] init', { roomId });
  }

  _buildPeerOptions(userOptions = {}) {
    const isProduction = process.env.NODE_ENV === 'production';
    // Use env var, or fallback to current hostname (works for any domain)
    const host = process.env.REACT_APP_PEER_HOST || 
                 (typeof window !== 'undefined' ? window.location.hostname : 'localhost');
    
    // In production (behind Nginx), use standard HTTPS port (443) implicitly
    // Don't specify port explicitly - let browser use default for https://
    // In development, use custom port 4002
    const port = process.env.REACT_APP_PEER_PORT ? 
                 parseInt(process.env.REACT_APP_PEER_PORT, 10) :
                 (isProduction ? 443 : 4002);
    
    const secure = process.env.REACT_APP_PEER_SECURE === 'true' || 
                   isProduction || 
                   (typeof window !== 'undefined' && window.location.protocol === 'https:');

    const defaultIceServers = [
      { urls: 'stun:stun.ankushgautam.in:3478' },
      { urls: 'turn:stun.ankushgautam.in:3478?transport=udp', username: 'coturn', credential: 'C0turn@' },
      { urls: 'turns:stun.ankushgautam.in:5349?transport=tcp', username: 'coturn', credential: 'C0turn@' },
      ...(process.env.REACT_APP_TURN_SERVER ? [{
        urls: process.env.REACT_APP_TURN_SERVER,
        username: process.env.REACT_APP_TURN_USERNAME || '',
        credential: process.env.REACT_APP_TURN_CREDENTIAL || ''
      }] : []),
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ];

    const options = {
      host,
      path: '/peerjs',  // Must match server PeerJS path exactly
      secure,
      debug: process.env.NODE_ENV === 'development' ? 2 : 0,
      config: {
        iceServers: userOptions?.config?.iceServers || defaultIceServers,
        sdpSemantics: 'unified-plan',
        ...userOptions?.config
      },
      ...userOptions
    };
    
    // Only add port in development or if explicitly configured
    // In production behind Nginx, omit port to use standard 443
    if (!isProduction || process.env.REACT_APP_PEER_PORT) {
      options.port = port;
    }
    
    return options;
  }

  on(ev, fn) { return this.emitter.on(ev, fn); }
  off(ev, fn) { this.emitter.off(ev, fn); }
  emit(ev, payload) { this.emitter.emit(ev, payload); }

  getPeer() { return this.peer; }
  getPeerId() { return this.peer?.id; }
  getConnections() { return this.connections; }
  getUsers() { return this.users; }

  async start() {
    if (this._started) {
      console.log('[ConnectionManager] start() ignored (already started)');
      return this.peer?.id;
    }
    this._started = true;
    
    try {
      const Peer = await getPeerCtor();
      if (!Peer) throw new Error('PeerJS not available');
      
      console.log('[ConnectionManager] Creating new Peer instance...');
      const opts = this.peerOptions;
      this._peerOpts = opts;
      
      console.log('[ConnectionManager] Connecting to PeerJS server:', {
        host: opts.host,
        port: opts.port,
        path: opts.path,
        secure: opts.secure
      });

      // Try to reuse saved peer ID from sessionStorage
      const savedPeerId = sessionStorage.getItem('peerId');
      if (savedPeerId) {
        console.log('[ConnectionManager] Reusing saved peer ID:', savedPeerId);
      } else {
        console.log('[ConnectionManager] No saved peer ID, generating new one');
      }
      this.peer = new Peer(savedPeerId || undefined, opts);

      this.peer.on('open', (peerId) => {
        sessionStorage.setItem('peerId', peerId);
        console.log('[ConnectionManager] peer open', { peerId });
        this.emit('open', { peerId });
        this.emit('connection:ready');
        
        // Initialize Socket.IO first, then join room
        this.initSocket().then(() => {
          // Socket.IO will trigger joinRoom on connect
          console.log('[ConnectionManager] Socket.IO initialized');
        }).catch(() => {
          // Fallback to HTTP if Socket.IO fails
          console.log('[ConnectionManager] Socket.IO unavailable, using HTTP fallback');
          this.joinRoom(peerId);
        });
      });

      this.peer.on('disconnected', () => {
        console.warn('[ConnectionManager] peer disconnected');
        this.emit('peer:disconnected');
        // Reconnect immediately instead of waiting for the 10s keep-alive poll
        if (!this._destroyed && this.peer && !this.peer.destroyed) {
          try {
            console.log('[ConnectionManager] Immediate peer.reconnect() after disconnect');
            this.peer.reconnect();
          } catch (_) {}
        }
      });
      
      this.peer.on('close', () => {
        console.warn('[ConnectionManager] peer close');
        this.emit('peer:close');
        // Peer is dead — full re-init is the only recovery path
        if (!this._destroyed) {
          this._scheduleReinit();
        }
      });
      
      this.peer.on('error', (err) => {
        // Filter out non-critical errors
        if (err && err.type === 'peer-unavailable') {
          console.warn('[ConnectionManager] Peer unavailable (already disconnected):', err.message);
          return; // Ignore - peer already left
        }
        
        console.error('[ConnectionManager] peer error', err);
        
        if (err && err.type === 'unavailable-id') {
          console.warn('[ConnectionManager] Unexpected ID conflict');
          sessionStorage.removeItem('peerId');
          this._started = false;
          setTimeout(() => {
            this.start().catch(e => console.error('Reconnect failed:', e));
          }, 1000);
        } else if (err && ['network', 'server-error', 'socket-error', 'socket-closed'].includes(err.type)) {
          // Fatal transport-level errors — PeerJS may destroy the peer; recover fully
          console.warn('[ConnectionManager] Fatal peer error type:', err.type, '- scheduling recovery');
          if (this.peer && this.peer.destroyed) {
            this._scheduleReinit();
          } else if (this.peer && this.peer.disconnected) {
            try { this.peer.reconnect(); } catch (_) { this._scheduleReinit(); }
          }
        }
        
        this.emit('error', err);
      });

      this.peer.on('connection', (conn) => {
        console.log('[ConnectionManager] incoming data connection', { from: conn.peer, label: conn.label });
        if (!this.connections[conn.peer]) this.connections[conn.peer] = {};
        
        if (conn.label === 'text') {
          this.#wireDataConnection(conn, 'text', true);
        } else if (conn.label === 'file') {
          this.#wireDataConnection(conn, 'file', true);
        } else {
          try { conn.close(); } catch (_) {}
        }
      });

      // Unified incoming call handler - dispatches to appropriate component
      this.peer.on('call', (call) => {
        console.log('[ConnectionManager] 📞 INCOMING CALL from:', call.peer, 'metadata:', call.metadata);
        
        const metadata = call.metadata || {};
        const callType = metadata.callType || 'video';
        
        console.log('[ConnectionManager] Dispatching call - type:', callType, 'to peer:', call.peer);
        
        // Emit event based on call type for components to handle
        if (callType === 'screen') {
          this.emit('call:incoming:screen', { call, peerId: call.peer, metadata });
        } else if (callType === 'audio') {
          this.emit('call:incoming:audio', { call, peerId: call.peer, metadata });
        } else if (callType === 'video') {
          this.emit('call:incoming:video', { call, peerId: call.peer, metadata });
        } else {
          // Unknown call type - emit generic event
          console.warn('[ConnectionManager] Unknown call type:', callType);
          this.emit('call:incoming', { call, peerId: call.peer, metadata, callType });
        }
      });

      // Heartbeat and keep-alive started after socket connect
      this._startKeepAlive(); // Always start peer reconnect polling
      this._bindNetworkListeners(); // React to online/offline and tab visibility
      
    } catch (error) {
      this._started = false;
      console.error('[ConnectionManager] Error during peer initialization:', error);
      this.emit('error', { source: 'start', error });
      throw error;
    }
  }

  async initSocket() {
    try {
      const ioLib = await getSocketIO();
      if (!ioLib) {
        console.warn('[ConnectionManager] Socket.IO not available, using HTTP polling only');
        return;
      }

      let socketUrl = process.env.REACT_APP_API_BASE || '';
      if (!socketUrl && this._peerOpts) {
        const proto = this._peerOpts.secure ? 'https' : 'http';
        const host = this._peerOpts.host || (typeof window !== 'undefined' ? window.location.hostname : 'localhost');
        
        // In production (behind Nginx), don't add port - let Nginx handle routing
        // In development, add port if it's not standard (80/443)
        let port = '';
        const portNum = this._peerOpts.port;
        const isProduction = process.env.NODE_ENV === 'production';
        const isStandardPort = (proto === 'https' && portNum === 443) || (proto === 'http' && portNum === 80);
        
        if (!isProduction && portNum && !isStandardPort) {
          port = `:${portNum}`;
        }
        
        socketUrl = `${proto}://${host}${port}`;
      }

      console.log('[ConnectionManager] Connecting to Socket.IO server:', socketUrl);
      console.log('[ConnectionManager] Socket.IO config:', {
        url: socketUrl,
        secure: this._peerOpts?.secure,
        host: this._peerOpts?.host,
        port: this._peerOpts?.port
      });
      
      this.socket = ioLib(socketUrl, {
        transports: ['websocket', 'polling'], // WebSocket first (PeerJS now on separate port 9000)
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
        timeout: 20000,
        // Path must match server Socket.IO path
        path: '/socket.io/'
      });

      this.socket.on('connect', () => {
        console.log('[ConnectionManager] Socket.IO connected successfully', {
          transport: this.socket.io?.engine?.transport?.name,
          reconnecting: this._socketReconnecting
        });
        
        // Only join room if this is NOT a reconnection (already joined via HTTP)
        if (this.roomId && this.peer?.id && !this._joinedRoom) {
          console.log('[ConnectionManager] First Socket.IO connect - calling joinRoom via HTTP');
          this.joinRoom(this.peer.id);
        } else if (this._socketReconnecting && this.roomId && this.peer?.id) {
          // On reconnection, just emit join-room (don't call HTTP joinRoom again)
          console.log('[ConnectionManager] Socket.IO reconnected - re-emitting join-room');
          this.socket.emit('join-room', { roomId: this.roomId, peerId: this.peer.id });
        }
        
        this._socketReconnecting = false;
        
        // Start heartbeat to prevent stale user cleanup
        this._startHeartbeat();
        
        // Start connection health check if not already running
        this._startHealthCheck();
      });

      this.socket.on('connect_error', (error) => {
        console.error('[ConnectionManager] Socket.IO connection error:', {
          message: error.message,
          description: error.description,
          type: error.type,
          stack: error.stack,
          transport: this.socket?.io?.engine?.transport?.name,
          url: socketUrl
        });
        this._socketReconnecting = true; // Mark that we're in reconnection state
        
        // Log detailed diagnostics
        console.error('[ConnectionManager] WebSocket connection failed. Diagnostics:', {
          socketUrl,
          readyState: this.socket?.io?.engine?.transport?.ws?.readyState,
          protocol: typeof window !== 'undefined' ? window.location.protocol : 'unknown',
          isSecure: this._peerOpts?.secure,
          engineState: this.socket?.io?.engine?.readyState
        });
      });

      this.socket.on('disconnect', (reason) => {
        console.warn('[ConnectionManager] Socket.IO disconnected:', {
          reason,
          transport: this.socket?.io?.engine?.transport?.name,
          wasConnected: this.socket?.connected
        });
        
        // Only reset _joinedRoom if server intentionally disconnected us
        // Don't reset on transport errors or client-side disconnects (will auto-reconnect)
        if (reason === 'io server disconnect') {
          console.log('[ConnectionManager] Server forced disconnect - will rejoin room');
          this._joinedRoom = false;
        } else {
          console.log('[ConnectionManager] Transient disconnect - will reconnect and re-emit join-room');
          this._socketReconnecting = true;
        }
      });
      
      // Add error event listener for underlying transport
      this.socket.on('error', (error) => {
        console.error('[ConnectionManager] Socket.IO error event:', error);
      });
      
      // Monitor reconnection attempts
      this.socket.io.on('reconnect_attempt', (attemptNumber) => {
        console.log('[ConnectionManager] Reconnection attempt:', attemptNumber);
      });
      
      this.socket.io.on('reconnect_failed', () => {
        console.error('[ConnectionManager] All reconnection attempts failed');
      });

      this.socket.on('room-users', (roomData) => {
        const eventTime = Date.now();
        console.log('[ConnectionManager] Received room-users update:', roomData);
        this._lastRoomUsersUpdate = eventTime;
        
        if (roomData && roomData.users) {
          this.updateUsers(roomData.users);
          const myPeerId = this.peer?.id;
          roomData.users.forEach(user => {
            if (user.peerId && user.peerId !== myPeerId && user.status === 'active') {
              if (!this.connections[user.peerId] || !this.connections[user.peerId].text) {
                console.log('[ConnectionManager] Connecting to new user:', user.peerId);
                this.connectToUser(user.peerId);
              }
            }
          });
        }
      });

      // Signal-on-Join pattern: notify when a new user connects
      this.socket.on('user-connected', (userId) => {
        console.log('[ConnectionManager] 🆕 User connected to room:', userId);
        this.emit('user-connected', userId);
      });

      // Handle peer socket disconnection — schedule reconnect
      this.socket.on('peer-socket-disconnected', ({ peerId: disconnectedId }) => {
        console.log('[ConnectionManager] Peer socket disconnected:', disconnectedId);
        this._scheduleReconnect(disconnectedId, 3000);
      });

      // Handle peer-left — clean up and stop trying to reconnect
      this.socket.on('peer-left', ({ peerId: leftId }) => {
        console.log('[ConnectionManager] Peer left room:', leftId);
        this._cancelReconnect(leftId);
      });

    } catch (error) {
      console.warn('[ConnectionManager] Error initializing Socket.IO (will use HTTP fallback):', error.message);
      this.socket = null;
    }
  }

  async joinRoom(peerId) {
    if (!this.roomId) return;
    if (this._joinedRoom) {
      console.log('[ConnectionManager] joinRoom ignored - already joined');
      return;
    }
    
    try {
      let apiBase = process.env.REACT_APP_API_BASE || '';
      if (!apiBase && this._peerOpts) {
        const proto = this._peerOpts.secure ? 'https' : 'http';
        const host = this._peerOpts.host || (typeof window !== 'undefined' ? window.location.hostname : 'localhost');
        const port = (typeof this._peerOpts.port !== 'undefined') ? `:${this._peerOpts.port}` : '';
        apiBase = `${proto}://${host}${port}`;
      }
      
      const url = `${apiBase}/room/${this.roomId}/join`;
      console.log('[ConnectionManager] joinRoom ->', url, { roomId: this.roomId, peerId });
      
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ peerId })
      });
      
      console.log('[ConnectionManager] joinRoom status', res.status);
      let payload = null;
      try { payload = await res.json(); } catch(_) {}
      console.log('[ConnectionManager] joinRoom payload', payload);
      
      this._joinedRoom = true; // Mark as joined
      this._socketReconnecting = false; // Clear reconnection flag
      this.emit('room:joined', { roomId: this.roomId, peerId, payload });
      
      // Emit Socket.IO join-room event for Signal-on-Join pattern (only once per HTTP join)
      if (this.socket && this.socket.connected) {
        console.log('[ConnectionManager] HTTP join success - emitting Socket.IO join-room event:', { roomId: this.roomId, peerId });
        this.socket.emit('join-room', { roomId: this.roomId, peerId });
      } else {
        console.warn('[ConnectionManager] Socket.IO not connected yet, join-room will be emitted on connect');
      }
      
      // Connect to existing users
      const users = (payload && (payload.users || payload.peers || payload.members || (payload.data && payload.data.users))) || [];
      if (Array.isArray(users)) {
        const norm = users
          .map(u => (typeof u === 'string' ? { peerId: u } : u))
          .filter(u => u && u.peerId && u.peerId !== peerId);
        console.log('[ConnectionManager] connecting to existing users', norm.map(u => u.peerId));
        this.updateUsers(users);
        norm.forEach(u => this.connectToUser(u.peerId));
      }
      
      this.startUsersPolling();
      
    } catch (e) {
      console.warn('[ConnectionManager] joinRoom error', e);
      this.emit('warn', { source: 'joinRoom', error: e });
    }
  }

  connectToUser(peerId) {
    if (!this.peer || this.peer.destroyed) throw new Error('Peer not ready');
    if (!this.connections[peerId]) this.connections[peerId] = {};
    if (!this._dialing[peerId]) this._dialing[peerId] = { text: false, file: false };

    console.log(`[ConnectionManager] Attempting to connect to peer ${peerId}`, {
      existingConnections: Object.keys(this.connections[peerId] || {})
    });
    
    // Validate existing channels
    const ready = (dc) => !!dc && dc.open && (dc.dataChannel?.readyState ? dc.dataChannel.readyState === 'open' : true);
    ['text','file'].forEach(label => {
      const dc = this.connections[peerId]?.[label];
      if (dc && !ready(dc)) {
        console.warn(`[ConnectionManager] ${label} channel to ${peerId} is stale, closing`);
        try { dc.close(); } catch(_) {}
        try { delete this.connections[peerId][label]; } catch(_) {}
      }
    });
    
    // If both channels are healthy, skip
    if (ready(this.connections[peerId]?.text) && ready(this.connections[peerId]?.file)) {
      console.log(`[ConnectionManager] Already have healthy connections to ${peerId}`);
      return;
    }

    try {
      // Create text connection if needed
      if (!ready(this.connections[peerId]?.text) && !this._dialing[peerId].text) {
        this._dialing[peerId].text = true;
        // Auto-reset _dialing if connection never opens within 10s (prevents permanent block)
        if (this._dialingTimers[peerId + '_text']) clearTimeout(this._dialingTimers[peerId + '_text']);
        this._dialingTimers[peerId + '_text'] = setTimeout(() => {
          if (this._dialing[peerId]) this._dialing[peerId].text = false;
        }, 10000);
        const textConn = this.peer.connect(peerId, { 
          label: 'text',
          reliable: true,
          serialization: 'json',
          metadata: { type: 'text', from: this.peer.id }
        });
        console.log(`[ConnectionManager] Created text connection to ${peerId}`);
        this.#wireDataConnection(textConn, 'text', false);
      }
      
      // Create file connection if needed
      if (!ready(this.connections[peerId]?.file) && !this._dialing[peerId].file) {
        this._dialing[peerId].file = true;
        // Auto-reset _dialing if connection never opens within 10s
        if (this._dialingTimers[peerId + '_file']) clearTimeout(this._dialingTimers[peerId + '_file']);
        this._dialingTimers[peerId + '_file'] = setTimeout(() => {
          if (this._dialing[peerId]) this._dialing[peerId].file = false;
        }, 10000);
        const fileConn = this.peer.connect(peerId, { 
          label: 'file',
          reliable: true,
          serialization: 'binary',
          metadata: { type: 'file', from: this.peer.id }
        });
        console.log(`[ConnectionManager] Created file connection to ${peerId}`);
        this.#wireDataConnection(fileConn, 'file', false);
      }
    } catch (error) {
      console.error(`[ConnectionManager] Error connecting to peer ${peerId}:`, error);
      this._dialing[peerId].text = false;
      this._dialing[peerId].file = false;
      throw error;
    }
  }

  updateUsers(users) {
    this.users = Array.isArray(users) ? users : [];
    this.usersByPeerId = {};
    this.users.forEach(u => { if (u?.peerId) this.usersByPeerId[u.peerId] = u; });
    this.emit('users:update', this.users);
  }

  async fetchUsersOnce() {
    try {
      let apiBase = process.env.REACT_APP_API_BASE || '';
      if (!apiBase && this._peerOpts) {
        const proto = this._peerOpts.secure ? 'https' : 'http';
        const host = this._peerOpts.host || (typeof window !== 'undefined' ? window.location.hostname : 'localhost');
        const port = (typeof this._peerOpts.port !== 'undefined') ? `:${this._peerOpts.port}` : '';
        apiBase = `${proto}://${host}${port}`;
      }
      const url = `${apiBase}/room/${this.roomId}/users`;
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) return;
      const data = await res.json();
      const users = (data && (data.users || data.members || data.peers)) || [];
      this.updateUsers(users);
      users.forEach(u => {
        if (u && u.peerId && u.peerId !== this.getPeerId()) {
          const already = this.connections[u.peerId]?.text && this.connections[u.peerId]?.file;
          if (!already) {
            console.log('[ConnectionManager] polling connect to', u.peerId);
            this.connectToUser(u.peerId);
          }
        }
      });
    } catch (e) {
      console.warn('[ConnectionManager] fetchUsersOnce error', e);
    }
  }

  startUsersPolling() {
    // Don't start HTTP polling if Socket.IO is connected (use real-time updates instead)
    if (this.socket && this.socket.connected) {
      console.log('[ConnectionManager] Socket.IO active - skipping HTTP polling (using real-time updates)');
      return;
    }
    
    if (this._usersPoll) return;
    console.log('[ConnectionManager] Starting HTTP polling (Socket.IO not available)');
    this._usersPoll = setInterval(() => {
      // Only poll if Socket.IO is NOT connected
      if (!this.socket || !this.socket.connected) {
        this.fetchUsersOnce();
      }
    }, 20000);
    this.fetchUsersOnce();
  }

  _startHeartbeat() {
    if (this.pingInterval) return;
    this.pingInterval = setInterval(() => {
      // Send PeerJS data channel pings
      Object.entries(this.connections).forEach(([peerId, channels]) => {
        const textDC = channels.text;
        if (textDC && textDC.open) {
          try { textDC.send({ type: 'ping', ts: Date.now() }); } catch (_) {}
        }
      });
      
      // Send Socket.IO heartbeat to keep user alive in room
      if (this.socket && this.socket.connected && this.roomId && this.peer?.id) {
        try {
          this.socket.emit('heartbeat', { roomId: this.roomId, peerId: this.peer.id });
        } catch (_) {}
      }
    }, 30000); // Every 30 seconds
  }

  _startKeepAlive() {
    if (this.keepAliveInterval) return;
    this.keepAliveInterval = setInterval(() => {
      if (this.peer && this.peer.disconnected && !this.peer.destroyed) {
        try {
          console.log('[ConnectionManager] Reconnecting peer...');
          this.peer.reconnect();
        } catch (_) {}
      }
    }, 10000);
  }

  _scheduleReconnect(peerId, delayMs = 2000) {
    if (!peerId || this._destroyed) return;
    // Cancel any existing pending reconnect for this peer
    if (this._reconnectTimers[peerId]) {
      clearTimeout(this._reconnectTimers[peerId]);
    }
    this._reconnectTimers[peerId] = setTimeout(async () => {
      delete this._reconnectTimers[peerId];
      if (this._destroyed) return;
      // Don't burn attempts while offline — retry when 'online' fires
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        console.log(`[ConnectionManager] Offline - postponing reconnect to ${peerId}`);
        return;
      }
      // If roster is missing/stale, refresh it before deciding the peer left
      let user = this.usersByPeerId[peerId];
      if (!user) {
        try { await this.fetchUsersOnce(); } catch (_) {}
        user = this.usersByPeerId[peerId];
      }
      if (!user || user.status !== 'active') {
        console.log(`[ConnectionManager] Peer ${peerId} no longer active - stopping reconnect`);
        delete this._reconnectAttempts[peerId];
        return;
      }
      if (!this.peer || this.peer.destroyed) {
        // Local peer is dead — re-init will re-dial everyone
        this._scheduleReinit();
        return;
      }
      const attempt = (this._reconnectAttempts[peerId] || 0) + 1;
      this._reconnectAttempts[peerId] = attempt;
      try {
        console.log(`[ConnectionManager] Auto-reconnecting to ${peerId} (attempt ${attempt})`);
        this.connectToUser(peerId);
      } catch (e) {
        console.warn(`[ConnectionManager] Reconnect to ${peerId} failed:`, e.message);
      }
      // Verify the channel actually opened; if not, retry with exponential backoff.
      // After MAX attempts, the 15s health check keeps trying (and resets the ladder on success).
      setTimeout(() => {
        if (this._destroyed) return;
        const ready = (dc) => !!dc && dc.open && (dc.dataChannel?.readyState ? dc.dataChannel.readyState === 'open' : true);
        const ok = ready(this.connections[peerId]?.text) && ready(this.connections[peerId]?.file);
        if (ok) {
          delete this._reconnectAttempts[peerId];
          return;
        }
        if (attempt < MAX_RECONNECT_ATTEMPTS) {
          const backoff = Math.min(RECONNECT_BACKOFF_BASE * Math.pow(2, attempt), RECONNECT_BACKOFF_CAP);
          console.log(`[ConnectionManager] Reconnect to ${peerId} not verified, retrying in ${backoff}ms`);
          this._scheduleReconnect(peerId, backoff);
        } else {
          console.warn(`[ConnectionManager] Max reconnect attempts reached for ${peerId} - health check will keep trying`);
          delete this._reconnectAttempts[peerId];
        }
      }, 8000);
    }, delayMs);
  }

  _cancelReconnect(peerId) {
    if (this._reconnectTimers[peerId]) {
      clearTimeout(this._reconnectTimers[peerId]);
      delete this._reconnectTimers[peerId];
    }
    delete this._reconnectAttempts[peerId];
  }

  // Full peer re-initialization: destroy dead peer, re-create, rejoin room, reconnect channels.
  // Emits 'peer:reinitialized' so call components can rejoin active calls.
  _scheduleReinit(delayMs) {
    if (this._destroyed || this._reinitializing || this._reinitTimer) return;
    const attempt = this._reinitAttempts + 1;
    const delay = typeof delayMs === 'number'
      ? delayMs
      : Math.min(RECONNECT_BACKOFF_BASE * Math.pow(2, this._reinitAttempts), RECONNECT_BACKOFF_CAP);
    console.warn(`[ConnectionManager] Scheduling full peer re-init in ${delay}ms (attempt ${attempt})`);
    this._reinitTimer = setTimeout(() => {
      this._reinitTimer = null;
      this._reinitPeer();
    }, delay);
  }

  async _reinitPeer() {
    if (this._destroyed || this._reinitializing) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Wait for the 'online' event to retry — don't burn attempts while offline
      console.log('[ConnectionManager] Offline - deferring peer re-init until network returns');
      return;
    }
    this._reinitializing = true;
    this._reinitAttempts += 1;
    console.warn(`[ConnectionManager] Re-initializing peer (attempt ${this._reinitAttempts})`);
    this.emit('peer:reinit:start');
    try {
      // Tear down old peer and connections (keep socket — it reconnects on its own)
      if (this.peer) {
        try { this.peer.destroy(); } catch (_) {}
        this.peer = null;
      }
      Object.values(this.connections).forEach(channels => {
        Object.values(channels).forEach(conn => { try { conn.close(); } catch (_) {} });
      });
      this.connections = {};
      this._dialing = {};
      Object.values(this._dialingTimers).forEach(t => clearTimeout(t));
      this._dialingTimers = {};
      Object.values(this._reconnectTimers).forEach(t => clearTimeout(t));
      this._reconnectTimers = {};
      this._reconnectAttempts = {};

      // Allow start() to run again and rejoin the room
      this._started = false;
      this._joinedRoom = false;

      await this.start();

      // Wait for the new peer to open (start() resolves before 'open' fires)
      await new Promise((resolve, reject) => {
        if (this.peer?.id && !this.peer.disconnected) return resolve();
        const timeout = setTimeout(() => { off(); reject(new Error('Peer open timeout')); }, 20000);
        const off = this.on('open', () => { clearTimeout(timeout); off(); resolve(); });
      });

      console.log('[ConnectionManager] ✅ Peer re-initialized successfully:', this.peer?.id);
      this._reinitAttempts = 0;
      this._reinitializing = false;
      this.emit('peer:reinitialized', { peerId: this.peer?.id });
    } catch (e) {
      console.error('[ConnectionManager] Peer re-init failed:', e);
      this._reinitializing = false;
      if (this._reinitAttempts < MAX_RECONNECT_ATTEMPTS * 2) {
        this._scheduleReinit();
      } else {
        console.error('[ConnectionManager] Giving up on automatic peer re-init');
        this.emit('peer:reinit:failed');
      }
    }
  }

  // React to browser network and tab-visibility changes for instant recovery
  _bindNetworkListeners() {
    if (this._netListenersBound || typeof window === 'undefined') return;
    this._netListenersBound = true;

    this._boundOnOnline = () => {
      console.log('[ConnectionManager] 🌐 Network back online - forcing recovery');
      if (this._destroyed) return;
      // Recover local peer
      if (this.peer && this.peer.destroyed) {
        this._scheduleReinit(500);
      } else if (this.peer && this.peer.disconnected) {
        try { this.peer.reconnect(); } catch (_) { this._scheduleReinit(500); }
      }
      // Recover socket
      if (this.socket && !this.socket.connected) {
        try { this.socket.connect(); } catch (_) {}
      }
      // Re-verify all peer channels shortly after transports recover
      setTimeout(() => this._runHealthCheckNow(), 2000);
    };

    this._boundOnOffline = () => {
      console.warn('[ConnectionManager] 🌐 Network offline - pausing reconnect attempts');
      // Cancel pending per-peer retries; they'll be re-triggered on 'online' / health check
      Object.values(this._reconnectTimers).forEach(t => clearTimeout(t));
      this._reconnectTimers = {};
    };

    this._boundOnVisibility = () => {
      if (document.visibilityState === 'visible' && !this._destroyed) {
        console.log('[ConnectionManager] Tab visible - running immediate health check');
        // Timers are throttled in background tabs; force recovery on foreground
        if (this.peer && this.peer.destroyed) {
          this._scheduleReinit(500);
        } else if (this.peer && this.peer.disconnected) {
          try { this.peer.reconnect(); } catch (_) {}
        }
        if (this.socket && !this.socket.connected) {
          try { this.socket.connect(); } catch (_) {}
        }
        this._runHealthCheckNow();
      }
    };

    window.addEventListener('online', this._boundOnOnline);
    window.addEventListener('offline', this._boundOnOffline);
    document.addEventListener('visibilitychange', this._boundOnVisibility);
  }

  _unbindNetworkListeners() {
    if (!this._netListenersBound || typeof window === 'undefined') return;
    this._netListenersBound = false;
    if (this._boundOnOnline) window.removeEventListener('online', this._boundOnOnline);
    if (this._boundOnOffline) window.removeEventListener('offline', this._boundOnOffline);
    if (this._boundOnVisibility) document.removeEventListener('visibilitychange', this._boundOnVisibility);
  }

  // One immediate health-check pass (same logic as the interval)
  _runHealthCheckNow() {
    if (!this.peer || this.peer.destroyed || this._destroyed) return;
    const myId = this.peer.id;
    this.users.forEach(user => {
      if (!user || !user.peerId || user.peerId === myId) return;
      if (user.status !== 'active') return;
      const peerId = user.peerId;
      const channels = this.connections[peerId] || {};
      const ready = (dc) => !!dc && dc.open && (dc.dataChannel?.readyState ? dc.dataChannel.readyState === 'open' : true);
      if (!ready(channels.text) || !ready(channels.file)) {
        console.log(`[ConnectionManager] Immediate health check: reconnecting to ${peerId}`);
        try { this.connectToUser(peerId); } catch (_) {}
      }
    });
  }

  _startHealthCheck() {
    if (this._healthCheckInterval) return;
    this._healthCheckInterval = setInterval(() => {
      if (this._destroyed) return;
      // Recover local peer/socket if they died silently
      if (this.peer && this.peer.destroyed) {
        this._scheduleReinit();
        return;
      }
      if (this.socket && !this.socket.connected && (typeof navigator === 'undefined' || navigator.onLine !== false)) {
        try { this.socket.connect(); } catch (_) {}
      }
      if (!this.peer || this.peer.destroyed) return;
      const myId = this.peer.id;
      const now = Date.now();
      this.users.forEach(user => {
        if (!user || !user.peerId || user.peerId === myId) return;
        if (user.status !== 'active') return;
        const peerId = user.peerId;
        const channels = this.connections[peerId] || {};
        const ready = (dc) => !!dc && dc.open && (dc.dataChannel?.readyState ? dc.dataChannel.readyState === 'open' : true);
        const textOk = ready(channels.text);
        const fileOk = ready(channels.file);
        
        // Check staleness - if no data received in 60s, force reconnect
        const lastSeen = this._peerLastSeen[peerId] || 0;
        const stale = (now - lastSeen) > 60000;
        
        // Also check ICE state on peer connection
        const pc = channels.text?.peerConnection;
        const iceFailed = pc && (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed');
        
        if (!textOk || !fileOk || stale || iceFailed) {
          console.log(`[ConnectionManager] Health check: reconnecting to ${peerId} (text:${textOk}, file:${fileOk}, stale:${stale}, ice:${pc?.iceConnectionState || 'n/a'})`);
          // Reset the backoff ladder so persistent failures keep getting fresh attempts
          delete this._reconnectAttempts[peerId];
          try { this.connectToUser(peerId); } catch(_) {}
        }
      });
    }, 15000); // Every 15s for faster recovery
  }

  #wireDataConnection(conn, label, incoming) {
    const connectionId = conn.connectionId;
    const peerId = conn.peer;
    const connectionKey = `${peerId}-${label}-${incoming ? 'in' : 'out'}`;
    
    console.log(`[ConnectionManager] [${connectionKey}] Setting up ${incoming ? 'incoming' : 'outgoing'} connection`, {
      connectionId,
      open: conn.open,
      dataChannel: label,
      peerConnection: !!conn.peerConnection
    });

    this.connections[peerId][label] = conn;
    if (this._dialing[peerId]) this._dialing[peerId][label] = false;

    conn.on('open', () => {
      console.log(`[ConnectionManager] [${connectionKey}] Connection opened`, {
        connectionId,
        open: conn.open,
        connectionState: conn.peerConnection?.connectionState,
        iceConnectionState: conn.peerConnection?.iceConnectionState,
        iceGatheringState: conn.peerConnection?.iceGatheringState,
        signalingState: conn.peerConnection?.signalingState
      });
      this.emit('dc:open', { peerId, label, connectionId });
      this.emit('peer:connected', { peerId });
    });
    
    conn.on('data', (data) => {
      console.log(`[ConnectionManager] [${connectionKey}] Received data:`, data);
      this._peerLastSeen[peerId] = Date.now();
      this.emit('dc:data', { peerId, label, data });
      
      // Emit specific events for call state messages
      if (data && data.type) {
        if (data.type === 'call-state-update' || 
            data.type === 'request-call-state' || 
            data.type === 'call-state-response') {
          console.log(`[ConnectionManager] Emitting ${data.type} event from ${peerId}`);
          this.emit(data.type, { peerId, data });
        }
      }
    });
    
    conn.on('close', () => {
      console.log(`[ConnectionManager] [${connectionKey}] Connection closed`);
      this.emit('dc:close', { peerId, label });
      if (this.connections[peerId] && this.connections[peerId][label] === conn) {
        delete this.connections[peerId][label];
      }
      // Schedule reconnect if peer is still in the room
      this._scheduleReconnect(peerId, 2000);
    });
    
    conn.on('error', (err) => {
      console.error(`[ConnectionManager] [${connectionKey}] Connection error:`, err);
      this.emit('dc:error', { peerId, label, error: err });
      // Reset dialing flag on error so retry is possible
      if (this._dialing[peerId]) this._dialing[peerId][label] = false;
    });
    
    // ICE connection state monitoring for proactive failure recovery
    if (conn.peerConnection) {
      conn.peerConnection.oniceconnectionstatechange = () => {
        const iceState = conn.peerConnection?.iceConnectionState;
        console.log(`[ConnectionManager] [${connectionKey}] ICE state: ${iceState}`);
        
        if (iceState === 'disconnected') {
          // Start a grace timer - ICE may recover on its own
          if (this._iceDisconnectTimers[peerId]) clearTimeout(this._iceDisconnectTimers[peerId]);
          this._iceDisconnectTimers[peerId] = setTimeout(() => {
            console.warn(`[ConnectionManager] [${connectionKey}] ICE disconnected for 10s, forcing reconnect`);
            // Close stale connection and reconnect
            try { conn.close(); } catch(_) {}
            if (this.connections[peerId] && this.connections[peerId][label] === conn) {
              delete this.connections[peerId][label];
            }
            this._scheduleReconnect(peerId, 2000);
          }, 10000);
        } else if (iceState === 'failed') {
          // ICE failed - immediate reconnect
          console.error(`[ConnectionManager] [${connectionKey}] ICE failed, forcing reconnect`);
          if (this._iceDisconnectTimers[peerId]) {
            clearTimeout(this._iceDisconnectTimers[peerId]);
            delete this._iceDisconnectTimers[peerId];
          }
          try { conn.close(); } catch(_) {}
          if (this.connections[peerId] && this.connections[peerId][label] === conn) {
            delete this.connections[peerId][label];
          }
          this._scheduleReconnect(peerId, 1000);
        } else if (iceState === 'connected' || iceState === 'completed') {
          // ICE recovered - clear any pending disconnect timer
          if (this._iceDisconnectTimers[peerId]) {
            clearTimeout(this._iceDisconnectTimers[peerId]);
            delete this._iceDisconnectTimers[peerId];
          }
        }
      };
    }
    
    
    if (conn.open) {
      console.log(`[ConnectionManager] [${connectionKey}] Connection initialized`, {
        connectionId,
        open: conn.open,
        connectionState: conn.peerConnection?.connectionState || 'new',
        iceConnectionState: conn.peerConnection?.iceConnectionState || 'new',
        iceGatheringState: conn.peerConnection?.iceGatheringState || 'new',
        signalingState: conn.peerConnection?.signalingState || 'stable'
      });
      
      this.emit('dc:open', { peerId, label, connectionId });
      this.emit('peer:connected', { peerId });
    }
  }

  broadcastData(message) {
    // Send message to all connected peers via text data channel
    Object.entries(this.connections).forEach(([peerId, channels]) => {
      const textDC = channels.text;
      if (textDC && textDC.open) {
        try {
          textDC.send(message);
          console.log(`[ConnectionManager] Broadcast to ${peerId}:`, message.type);
        } catch (err) {
          console.error(`[ConnectionManager] Failed to broadcast to ${peerId}:`, err);
        }
      }
    });
  }

  disconnect() {
    this._destroyed = true;
    this._unbindNetworkListeners();
    if (this._reinitTimer) { clearTimeout(this._reinitTimer); this._reinitTimer = null; }
    if (this.pingInterval) clearInterval(this.pingInterval);
    if (this.keepAliveInterval) clearInterval(this.keepAliveInterval);
    if (this._usersPoll) clearInterval(this._usersPoll);
    if (this._healthCheckInterval) clearInterval(this._healthCheckInterval);
    Object.values(this._reconnectTimers || {}).forEach(t => clearTimeout(t));
    Object.values(this._dialingTimers || {}).forEach(t => clearTimeout(t));
    Object.values(this._iceDisconnectTimers || {}).forEach(t => clearTimeout(t));
    this._reconnectTimers = {};
    this._dialingTimers = {};
    this._iceDisconnectTimers = {};
    this._reconnectAttempts = {};
    
    Object.values(this.connections).forEach(channels => {
      Object.values(channels).forEach(conn => {
        try { conn.close(); } catch (_) {}
      });
    });
    
    if (this.socket) {
      try { this.socket.disconnect(); } catch (_) {}
    }
    
    if (this.peer) {
      try { this.peer.destroy(); } catch (_) {}
    }
    
    this.connections = {};
    this._started = false;
    this._joinedRoom = false;
  }
}
